import type {
	PictureConfiguration,
	Palette,
	BrickCounts,
	BuildBitmap,
} from "../model/picture.ts";
import { bitmapChannels } from "../bitmap/types.ts";

function* countBricks(
	systemPalette: Palette,
	config: PictureConfiguration,
	image: BuildBitmap,
): Generator<undefined, BrickCounts> {
	// Create direct mapping for faster lookup
	const colourMap = new Map<number, string>();
	for (const item of systemPalette) {
		colourMap.set(item.brick.rgba, item.brick.hexString);
	}

	// Create a Map to store pixel counts directly by rgba value
	const pixelCountsByRgba = new Map<number, number>();
	const { data, width, height } = image;
	const noBrickColour = image.noBrickColour?.rgba;
	const totalPixels = width * height;

	// Process all pixels in a single pass for better performance
	for (let pixel = 0; pixel < totalPixels; pixel++) {
		const offset = pixel * bitmapChannels;

		// Inline readUInt32BE for better performance
		const rgba =
			data[offset] * 0x1000000 +
			((data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3]);

		// Skip if it's a noBrickColour
		if (rgba !== noBrickColour) {
			// Update the count in our Map
			pixelCountsByRgba.set(rgba, (pixelCountsByRgba.get(rgba) ?? 0) + 1);
		}
	}

	// A single yield point after processing all pixels
	yield;

	// Convert rgba counts to hex string counts
	const brickCounts: BrickCounts = {};

	// Process the collected pixel counts
	for (const [rgba, count] of pixelCountsByRgba.entries()) {
		const hexString = colourMap.get(rgba);
		if (!hexString) {
			throw new Error(`Couldn't find rgba ${rgba}`);
		}
		brickCounts[hexString] = { type: "palette", count };
	}

	// Now handle pens if there are any
	if (config.pen.length > 0) {
		// Filter pens that are within the frame
		const penInFrame = config.pen.filter((p) => {
			const adjustedX = p.position.x + config.imageZoomOffset.x;
			if (adjustedX < 0 || adjustedX >= config.numberOfBricks.width) {
				return false;
			}
			const adjustedY = p.position.y + config.imageZoomOffset.y;
			return adjustedY >= 0 && adjustedY < config.numberOfBricks.height;
		});

		// Count pens by hex string
		const penCounts = new Map<string, number>();
		for (const pen of penInFrame) {
			const hexString = pen.colour.hexString;
			penCounts.set(hexString, (penCounts.get(hexString) || 0) + 1);
		}

		// Update brick counts based on pen counts
		for (const [hex, count] of penCounts.entries()) {
			if (hex in brickCounts) {
				const brickCount = brickCounts[hex].count;
				if (brickCount === count) {
					brickCounts[hex] = { type: "pen-only", count: brickCount };
				}
			}
		}
	}

	return brickCounts;
}

export { countBricks };
